import React from 'react';
import {
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  FirstPage,
  LastPage,
} from '@mui/icons-material';
import { useDeviceDataPeriod } from '../../../hooks/useDeviceDataPeriod';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMDatePicker } from '../MHM/MHMDatePicker';
import { MHMTypography } from '../MHM/MHMTypography';
import dayjs from 'dayjs';
import type { Period } from '../../../models/atoms/device_atoms';
import { Dayjs } from 'dayjs';
import { useDeviceDataDateRange } from '../../../hooks/useDeviceDataDateRange';

export function DeviceDataPeriodRangeSelect() {
  const { t } = useTranslation();
  const {
    periods,
    periodType,
    periodStartDate,
    periodEndDate,
    isEarliestPeriod,
    isLatestPeriod,
    canIncreasePeriod,
    canDecreasePeriod,
    changePeriodType,
    increasePeriod,
    decreasePeriod,
    earliestPeriod,
    latestPeriod,
    setPeriodStartDate,
    setPeriodEndDate,
  } = useDeviceDataPeriod();

  const { shouldDisableStartDate, shouldDisableEndDate } =
    useDeviceDataDateRange();

  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: Period | null,
  ) => {
    if (newPeriod !== null) {
      changePeriodType(newPeriod);
    }
  };

  const showNav = periodType !== 'all' && periodType !== 'custom';

  const formatDate = (date: Dayjs) => {
    return date.format('MMM D, YYYY HH:mm');
  };

  return (
    <Stack direction="column" spacing={0.5} sx={{ mt: 2, mb: 0 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        spacing={1}
      >
        <ToggleButtonGroup
          value={periodType}
          exclusive
          onChange={handlePeriodChange}
          size="small"
          color="primary"
        >
          {periods.map(([period, label]) => (
            <ToggleButton
              key={period}
              value={period}
              data-testid={`period-select-${period}`}
            >
              {label}
            </ToggleButton>
          ))}
          <ToggleButton value="all" data-testid="period-select-all">
            {t('All')}
          </ToggleButton>
          <ToggleButton value="custom" data-testid="period-select-custom">
            {t('Custom')}
          </ToggleButton>
        </ToggleButtonGroup>

        {showNav && (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('Earliest')}>
              <span>
                <IconButton
                  size="small"
                  onClick={earliestPeriod}
                  disabled={isEarliestPeriod}
                  data-testid="period-earliest"
                >
                  <FirstPage />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('Previous')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => decreasePeriod(periodType)}
                  disabled={!canDecreasePeriod(periodType)}
                  data-testid="period-back"
                >
                  <NavigateBefore />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('Next')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => increasePeriod(periodType)}
                  disabled={!canIncreasePeriod(periodType)}
                  data-testid="period-forward"
                >
                  <NavigateNext />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('Latest')}>
              <span>
                <IconButton
                  size="small"
                  onClick={latestPeriod}
                  disabled={isLatestPeriod}
                  data-testid="period-latest"
                >
                  <LastPage />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <Box sx={{ height: 2 }} />

      {periodType === 'custom' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <MHMDatePicker
            label={t('Start Date')}
            value={periodStartDate.toDate()}
            onChange={date => setPeriodStartDate(dayjs(date))}
            shouldDisableDate={shouldDisableStartDate}
          />
          <MHMDatePicker
            label={t('End Date')}
            value={periodEndDate.toDate()}
            onChange={date => setPeriodEndDate(dayjs(date))}
            shouldDisableDate={shouldDisableEndDate}
          />
        </Stack>
      )}

      {periodType !== 'custom' && (
        <MHMTypography
          variant="body2"
          color="textSecondary"
          data-testid="period-date-range"
        >
          {formatDate(periodStartDate)} - {formatDate(periodEndDate)}
        </MHMTypography>
      )}
    </Stack>
  );
}
